import { sessionStore } from '@/auth/session';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly corps?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE = '/api/v1';

/** Message lisible à partir d'une réponse d'erreur NestJS (chaîne, liste de validation ou objet). */
function messageDe(corps: unknown, statut: number): string {
  if (corps && typeof corps === 'object' && 'message' in corps) {
    const m = (corps as { message: unknown }).message;
    if (Array.isArray(m)) return m.join(' · ');
    if (typeof m === 'string') return m;
    if (m && typeof m === 'object' && 'message' in m) return String((m as { message: unknown }).message);
  }
  return statut === 401 ? 'Session expirée : reconnectez-vous.' : statut === 403 ? 'Action non autorisée pour votre profil.' : `Erreur ${statut}`;
}

export interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  corps?: unknown;
  /** Ne pas envoyer la session (connexion). */
  anonyme?: boolean;
}

/** Appel JSON vers l'API : session en en-tête, erreurs typées, déconnexion automatique sur 401. */
export async function api<T>(chemin: string, options: Options = {}): Promise<T> {
  const entetes: Record<string, string> = { Accept: 'application/json' };
  if (options.corps !== undefined) entetes['Content-Type'] = 'application/json';
  const session = sessionStore.get();
  if (!options.anonyme && session) entetes.Authorization = `Bearer ${session.jeton}`;
  const res = await fetch(`${BASE}${chemin}`, {
    method: options.method ?? (options.corps !== undefined ? 'POST' : 'GET'),
    headers: entetes,
    body: options.corps !== undefined ? JSON.stringify(options.corps) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const texte = await res.text();
  const corps: unknown = texte ? JSON.parse(texte) : null;
  if (!res.ok) {
    if (res.status === 401 && !options.anonyme) sessionStore.set(null);
    throw new ApiError(res.status, messageDe(corps, res.status), corps);
  }
  return corps as T;
}

/** Contrôles bloquants renvoyés par l'API lors d'une soumission refusée (400) : corps { message, controles }. */
export function controlesDe(e: unknown): Array<{ ok: boolean; message: string }> {
  if (e instanceof ApiError && e.corps && typeof e.corps === 'object' && 'controles' in e.corps) {
    const c = (e.corps as { controles?: unknown }).controles;
    if (Array.isArray(c)) return c as Array<{ ok: boolean; message: string }>;
  }
  return [];
}

/** Envoi d'un fichier (multipart) avec des champs : même session et mêmes erreurs typées que les appels JSON. */
export async function apiFichier<T>(chemin: string, champs: Record<string, string | undefined>, fichier: File): Promise<T> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(champs)) if (v !== undefined && v !== '') fd.set(k, v);
  fd.set('fichier', fichier, fichier.name);
  const session = sessionStore.get();
  const res = await fetch(`${BASE}${chemin}`, { method: 'POST', headers: { Accept: 'application/json', ...(session ? { Authorization: `Bearer ${session.jeton}` } : {}) }, body: fd });
  const texte = await res.text();
  let corps: unknown = null;
  try {
    corps = texte ? JSON.parse(texte) : null;
  } catch {
    corps = null;
  }
  if (!res.ok) {
    if (res.status === 401) sessionStore.set(null);
    throw new ApiError(res.status, res.status === 413 ? 'Fichier trop volumineux (20 Mo au plus).' : messageDe(corps, res.status), corps);
  }
  return corps as T;
}

/** Envoi de plusieurs fichiers (multipart, champ « fichiers ») : photos importées en lot. */
export async function apiFichiers<T>(chemin: string, fichiers: File[]): Promise<T> {
  const fd = new FormData();
  for (const f of fichiers) fd.append('fichiers', f, f.name);
  const session = sessionStore.get();
  const res = await fetch(`${BASE}${chemin}`, { method: 'POST', headers: { Accept: 'application/json', ...(session ? { Authorization: `Bearer ${session.jeton}` } : {}) }, body: fd });
  const texte = await res.text();
  let corps: unknown = null;
  try {
    corps = texte ? JSON.parse(texte) : null;
  } catch {
    corps = null;
  }
  if (!res.ok) {
    if (res.status === 401) sessionStore.set(null);
    throw new ApiError(res.status, res.status === 413 ? 'Fichiers trop volumineux.' : messageDe(corps, res.status), corps);
  }
  return corps as T;
}

/** Ouverture d'un document protégé (PDF) dans un nouvel onglet ; repli en téléchargement si la fenêtre est bloquée. */
export async function apiOuvrir(chemin: string, nom: string): Promise<void> {
  const fenetre = window.open('', '_blank');
  const session = sessionStore.get();
  const res = await fetch(`${BASE}${chemin}`, { headers: session ? { Authorization: `Bearer ${session.jeton}` } : {} });
  if (!res.ok) {
    fenetre?.close();
    let corps: unknown = null;
    try {
      corps = JSON.parse(await res.text());
    } catch {
      corps = null;
    }
    if (res.status === 401) sessionStore.set(null);
    throw new ApiError(res.status, messageDe(corps, res.status), corps);
  }
  const url = URL.createObjectURL(await res.blob());
  if (fenetre) {
    fenetre.location.href = url;
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = nom;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Lecture d'un fichier protégé en mémoire (lecteur intégré des procédures) : la session voyage en en-tête, jamais dans l'adresse. */
export async function apiBlob(chemin: string): Promise<Blob> {
  const session = sessionStore.get();
  const res = await fetch(`${BASE}${chemin}`, { headers: session ? { Authorization: `Bearer ${session.jeton}` } : {} });
  if (!res.ok) {
    let corps: unknown = null;
    try {
      corps = JSON.parse(await res.text());
    } catch {
      corps = null;
    }
    if (res.status === 401) sessionStore.set(null);
    throw new ApiError(res.status, messageDe(corps, res.status), corps);
  }
  return res.blob();
}

/** Téléchargement d'un fichier protégé : un simple lien ne porterait pas la session. */
export async function apiTelecharger(chemin: string, nom: string): Promise<void> {
  const session = sessionStore.get();
  const res = await fetch(`${BASE}${chemin}`, { headers: session ? { Authorization: `Bearer ${session.jeton}` } : {} });
  if (!res.ok) {
    let corps: unknown = null;
    try {
      corps = JSON.parse(await res.text());
    } catch {
      corps = null;
    }
    if (res.status === 401) sessionStore.set(null);
    throw new ApiError(res.status, messageDe(corps, res.status), corps);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

